/**
 * Where the user is in the CRM, as the Chief of Staff hears it: the page's path and view, the installed app and object
 * type it belongs to, and on a record page the record's id and name. Derived from the route and the tenant schema the
 * CRM renders from, so a page an installed app added is described as that app's. A hint, never authority: the API
 * takes the tenant and user from the token and looks the record up before changing it.
 */
import type { PageContext, PageView } from '@diggens/types';
import type { SchemaNodeType, TenantSchema } from '@diggens/types/apps';
import { MODULE_REGISTRY } from '@diggens/types/graph/modules';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The page without the record's name, which needs the record itself (see recordDisplayName). */
export type PagePlace = Omit<PageContext, 'recordName'>;

function recordIdAfter(path: string, listPath: string): string | undefined {
  if (!path.startsWith(`${listPath}/`)) return undefined;
  const rest = path.slice(listPath.length + 1);
  return UUID.test(rest) ? rest.toLowerCase() : undefined;
}

/** The place with the app its object type belongs to: the nav's app for an app page, else the type's own app (a redefined built-in). */
function withApp(place: PagePlace, schema: TenantSchema, appKey?: string): PagePlace {
  const key = appKey ?? (place.objectType ? schema.nodeTypes[place.objectType]?.appKey : undefined);
  return key ? { ...place, appKey: key } : place;
}

export function derivePageContext(pathname: string, schema: TenantSchema): PagePlace {
  const path = pathname.replace(/\/+$/, '') || '/';

  // Pages an installed app added, in the order the sidebar shows them
  for (const category of schema.nav) {
    for (const item of category.items) {
      const view: PageView = item.kind === 'kanban' ? 'board' : 'list';
      if (path === item.path) return withApp({ path, view, objectType: item.objectType }, schema, category.appKey);
      const recordId = recordIdAfter(path, item.path);
      if (recordId) return withApp({ path, view: 'record', objectType: item.objectType, recordId }, schema, category.appKey);
    }
  }

  // Built-in module pages; a redefined type still reports the app that redefined it
  for (const module of Object.values(MODULE_REGISTRY)) {
    for (const route of module.routes) {
      if (path === route.path) return withApp({ path, view: route.path.endsWith('/pipeline') ? 'board' : 'list', objectType: route.nodeType }, schema);
      const recordId = recordIdAfter(path, route.path);
      if (recordId) return withApp({ path, view: 'record', objectType: route.nodeType, recordId }, schema);
    }
  }

  if (path === '/' || path === '/home' || path.startsWith('/dashboards')) return { path, view: 'dashboard' };
  return { path, view: 'page' };
}

/** A record's display name as its page shows it: the type's title field (the first form field), else the usual name fields. */
export function recordDisplayName(props: Record<string, unknown> | undefined, type: SchemaNodeType | undefined): string | undefined {
  if (!props) return undefined;
  const candidates = [type?.formFields[0]?.key, 'name', 'title', 'subject'].filter((key): key is string => !!key);
  for (const key of candidates) {
    const value = props[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return undefined;
}
